import React, { useMemo, ReactElement } from 'react';
import Widget from '../../../components/widget/widget.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import TableCellExtended from '../../../components/table-cell-extended/table-cell-extended.component';
import useScreenSmall from '../../../hooks/use-screen-small';
import useScreenMedium from '../../../hooks/use-screen-medium';
import { SETTINGS_USER_VIEW } from '../../../constants/entity/permissions';
import { ROUTE_USER } from '../../../constants/system/routes';

const DealStatusChanges: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Deal Status Change History';
  const WIDGET_NAME: string = 'widget-deal-status-changes';

  const {
    dealStatusChangeListData,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const isMediumScreen: boolean = useScreenMedium();

  const isEmpty: boolean = !dealStatusChangeListData || !dealStatusChangeListData.length;

  const tableDealStatusChangeListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'createdDate',
      title: 'Date',
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        isMediumScreen ? (
          <TableCell isText>
            {dealStatusChangeItem.createdDate}
          </TableCell>
        ) : (
          <TableCellExtended isText>
            {dealStatusChangeItem.createdDate}
          </TableCellExtended>
        )
      ),
    },
    {
      id: 2,
      name: 'statusFrom',
      title: 'Status From',
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        <TableCell>
          {dealStatusChangeItem.dealStatusFromText}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'statusTo',
      title: 'Status To',
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        <TableCell>
          {dealStatusChangeItem.dealStatusToText}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'createdByUser',
      title: 'User',
      isHidden: isSmallScreen,
      component: (dealStatusChangeItem: DealStatusChange): ReactElement => (
        <TableCell isText>
          {
            dealStatusChangeItem.createdByUser && (
              <FilteredLink
                withNewWindow
                link={`${ROUTE_USER}/${dealStatusChangeItem.createdByUser.id}`}
                permission={SETTINGS_USER_VIEW}
              >
                {dealStatusChangeItem.createdByUser.fullName}
              </FilteredLink>
            )
          }
        </TableCell>
      ),
    },
  ], [isSmallScreen, isMediumScreen]);

  return (
    <Widget
      isFullWidth
      isFullHeight={isSmallScreen}
      isScrollable={!isSmallScreen}
      isEmpty={isEmpty}
      widgetName={WIDGET_NAME}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
    >
      <Table withStickyHeader>
        <TableHead isSubHeader>
          <TableRow>
            {
              tableDealStatusChangeListFields.map((tableField: TableField): ReactElement => (
                <VisibleCell key={tableField.id} tableField={tableField}>
                  <TableCell>
                    {tableField.title}
                  </TableCell>
                </VisibleCell>
              ))
            }
          </TableRow>
        </TableHead>
        <TableBody isPlain withZebraRows>
          {
            dealStatusChangeListData.map((dealStatusChangeItem: DealStatusChange): ReactElement => (
              <TableRow key={dealStatusChangeItem.id}>
                {
                  tableDealStatusChangeListFields.map((tableField: TableField): ReactElement | boolean => (
                    <VisibleCell key={tableField.id} tableField={tableField}>
                      {!!tableField.component && tableField.component(dealStatusChangeItem)}
                    </VisibleCell>
                  ))
                }
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    </Widget>
  );
});

export default DealStatusChanges;
